import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { messagingService } from '@services/messagingService';
import { chatSocket, CHAT_EVENTS } from '@services/chatSocket';
import type { Message } from '@/types';
import { useAuthStore } from '@store/authStore';
import { useChatNotificationStore } from '@store/chatNotificationStore';
import { clearInboxPartnerUnread, INBOX_QUERY_KEY } from '@utils/messagingCache';
import { UNREAD_MESSAGES_QUERY_KEY } from '@hooks/useUnreadMessages';

interface UseChatThreadOptions {
  partnerId: string;
  orderId?: string;
  enabled?: boolean;
}

function mergeMessages(existing: Message[], incoming: Message[]): Message[] {
  const map = new Map<string, Message>();
  for (const msg of existing) {
    map.set(msg.clientMessageId ?? msg.id, msg);
  }
  for (const msg of incoming) {
    map.set(msg.clientMessageId ?? msg.id, msg);
  }
  return Array.from(map.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

function isPartnerMessage(msg: Message, userId: string, partnerId: string): boolean {
  return (
    [msg.senderId, msg.receiverId].includes(partnerId) &&
    [msg.senderId, msg.receiverId].includes(userId)
  );
}

export function useChatThread({ partnerId, orderId, enabled = true }: UseChatThreadOptions) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const setActivePartnerId = useChatNotificationStore((s) => s.setActivePartnerId);
  const [realtimeMessages, setRealtimeMessages] = useState<Message[]>([]);
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const typingTimeoutRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const queryKey = ['messages', 'thread', partnerId] as const;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => messagingService.getPartnerMessages(partnerId),
    enabled: enabled && Boolean(partnerId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setRealtimeMessages([]);
  }, [partnerId]);

  const messages = useMemo(
    () => mergeMessages(data?.messages ?? [], realtimeMessages),
    [data?.messages, realtimeMessages],
  );

  useEffect(() => {
    if (!data || !partnerId) return;
    clearInboxPartnerUnread(queryClient, partnerId);
  }, [data, partnerId, queryClient]);

  useEffect(() => {
    if (!enabled || !partnerId || !userId) return;
    setActivePartnerId(partnerId);
    chatSocket.joinThread(partnerId);

    void messagingService.scheduleMarkRead({ partnerId, orderId }, 250).then(() => {
      clearInboxPartnerUnread(queryClient, partnerId);
      void queryClient.invalidateQueries({ queryKey: UNREAD_MESSAGES_QUERY_KEY });
    });

    const offNew = chatSocket.on(CHAT_EVENTS.MESSAGE_NEW, (payload) => {
      const msg = (payload as { message: Message }).message;
      if (!isPartnerMessage(msg, userId, partnerId)) return;

      setRealtimeMessages((prev) => mergeMessages(prev, [msg]));

      if (msg.receiverId === userId) {
        void messagingService.scheduleMarkRead({ partnerId, orderId }).then(() => {
          clearInboxPartnerUnread(queryClient, partnerId);
          void queryClient.invalidateQueries({ queryKey: UNREAD_MESSAGES_QUERY_KEY });
        });
      }
    });

    const offRead = chatSocket.on(CHAT_EVENTS.MESSAGE_READ, (payload) => {
      const { readerId, readAt } = payload as { readerId: string; readAt: string };
      if (readerId === userId) return;
      setRealtimeMessages((prev) =>
        prev.map((m) =>
          m.senderId === userId ? { ...m, isRead: true, readAt, status: 'read' as const } : m,
        ),
      );
    });

    const offTypingStart = chatSocket.on(CHAT_EVENTS.TYPING_START, (payload) => {
      const { userId: typingUserId } = payload as { userId: string };
      if (typingUserId === partnerId) setIsPartnerTyping(true);
    });

    const offTypingStop = chatSocket.on(CHAT_EVENTS.TYPING_STOP, (payload) => {
      const { userId: typingUserId } = payload as { userId: string };
      if (typingUserId === partnerId) setIsPartnerTyping(false);
    });

    return () => {
      offNew();
      offRead();
      offTypingStart();
      offTypingStop();
      chatSocket.leaveThread();
      setActivePartnerId(null);
    };
  }, [enabled, orderId, partnerId, queryClient, setActivePartnerId, userId]);

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const clientMessageId = uuidv4();
      const optimistic: Message = {
        id: clientMessageId,
        clientMessageId,
        senderId: userId!,
        receiverId: partnerId,
        content,
        orderId,
        isRead: false,
        createdAt: new Date().toISOString(),
        status: 'sent',
      };
      setRealtimeMessages((prev) => mergeMessages(prev, [optimistic]));

      const saved = await messagingService.sendMessage(partnerId, content, orderId, clientMessageId);
      setRealtimeMessages((prev) =>
        mergeMessages(
          prev.filter((m) => m.clientMessageId !== clientMessageId),
          [saved],
        ),
      );
      return saved;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
    },
  });

  const notifyTyping = useCallback(
    (isTyping: boolean) => {
      chatSocket.sendTyping(partnerId, orderId, isTyping);
      if (typingTimeoutRef.current) {
        window.clearTimeout(typingTimeoutRef.current);
      }
      if (isTyping) {
        typingTimeoutRef.current = window.setTimeout(() => {
          chatSocket.sendTyping(partnerId, orderId, false);
        }, 2500);
      }
    },
    [orderId, partnerId],
  );

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, scrollToBottom]);

  const threadTitle = useMemo(() => 'Mesajlar', []);

  return {
    messages,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    isPartnerTyping,
    sendMutation,
    notifyTyping,
    listRef,
    scrollToBottom,
    threadTitle,
  };
}
